import { makeStyles } from '@material-ui/core'
import LegendIcon from 'components/CopilotControl/icons/LegendIcon'
import Tooltip from 'components/common/Tooltip'
import DazzedHeadingForGrid from 'components/common/text/DazzedHeadingForGrid'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import BaseWidget from '../../components/common/BaseWidget'
import EngagementWidgetSkeleton from '../../components/common/EngagementWidget/EngagementWidgetSkeleton'
import SeePatientsFooter from '../../OralHygieneReport/widgets/SeePatientsFooter'
import ChartInfoIcon from './icons/ChartInfoIcon'
import ChartPageArrowIcon from './icons/ChartPageArrowIcon'

// Figma frame 220 chart geometry.
const BAR_WIDTH = 40
const Y_AXIS_WIDTH = 24
// Half of the bar value label, so the label of a full-height bar is not clipped.
const CHART_TOP_MARGIN = 9
// Half of an axis tick label, so the 0 tick is not clipped.
const CHART_BOTTOM_MARGIN = 6
// Figma frame 220 puts the legend squares 25px below the 0 line.
const LEGEND_OFFSET_FROM_ZERO_LINE = 25
const LEGEND_COLUMN_GAP = 16

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  container: {
    position: 'relative',
    flexGrow: 1,
    minHeight: 0,
    overflow: 'hidden',
    // Figma frame 220: title baseline 37px and the 100 line 67px below the card top.
    padding: '19.5px 24px 24px',
    display: 'flex',
    flexDirection: 'column'
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0
  },
  tooltip: {
    marginLeft: 8,
    display: 'flex'
  },
  chartContainer: {
    position: 'relative',
    flexGrow: 1,
    minHeight: 90,
    marginTop: 16.5
  },
  pageButton: {
    position: 'absolute',
    top: `calc(50% + ${(CHART_TOP_MARGIN - CHART_BOTTOM_MARGIN) / 2}px)`,
    transform: 'translateY(-50%)',
    display: 'flex',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer'
  },
  previousPageButton: {
    left: -15
  },
  nextPageButton: {
    right: -15
  },
  legend: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    columnGap: LEGEND_COLUMN_GAP,
    rowGap: 8,
    paddingTop: LEGEND_OFFSET_FROM_ZERO_LINE - CHART_BOTTOM_MARGIN
  },
  legendItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
    flex: '1 1 min-content',
    maxWidth: 'max-content',
    fontFamily: 'Dazzed',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1,
    color: '#6D6D71',
    '& svg': {
      flexShrink: 0
    }
  },
  errorState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  }
}))

/**
 * Value label rendered as a white rounded box centered on the top edge of its bar, as in Figma.
 */
const BarValueLabel = ({ x = 0, y = 0, width = 0, value }) => {
  const boxWidth = Math.max(24, String(value).length * 7 + 8)
  const boxHeight = 17
  const boxX = x + width / 2 - boxWidth / 2
  const boxY = y - boxHeight / 2

  return (
    <g>
      <rect x={boxX} y={boxY} width={boxWidth} height={boxHeight} rx={2} fill="#FFFFFF" stroke="#E9E9E9" />
      <text
        x={x + width / 2}
        y={boxY + boxHeight / 2 + 4}
        textAnchor="middle"
        fontFamily="Dazzed"
        fontSize={11}
        fontWeight={500}
        fill="#001335"
      >
        {value}
      </text>
    </g>
  )
}

/**
 * A Tx Tracking bar chart card: title with info tooltip, one 40px bar per category with its count,
 * a legend, and the hover "See patients" footer. Bars keep Figma's center-to-center spacing and are
 * centered in the plot. With `pageSize`, the categories are split into pages browsed by side arrows.
 */
const TxTrackingChartWidget = ({
  title,
  tooltip,
  bars = [],
  barSpacing,
  pageSize,
  isLoading,
  isError,
  isSelected,
  onSeePatients,
  onPageChanged = () => {}
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [hovered, setHovered] = useState(false)
  const [chartWidth, setChartWidth] = useState(0)
  const [page, setPage] = useState(0)

  const pagesCount = pageSize ? Math.max(1, Math.ceil(bars.length / pageSize)) : 1
  const currentPage = Math.min(page, pagesCount - 1)
  const pageBars = useMemo(
    () => (pageSize ? bars.slice(currentPage * pageSize, (currentPage + 1) * pageSize) : bars),
    [bars, pageSize, currentPage]
  )
  const hasAnyData = bars.some(bar => bar.value > 0)

  const plotWidth = Math.max(chartWidth - Y_AXIS_WIDTH, 0)
  const xAxisPadding = Math.max((plotWidth - pageBars.length * barSpacing) / 2, 0)

  // The chart subtree is memoized: recharts restarts (and cuts short) its bar animation whenever it
  // receives new child elements, and this widget re-renders on hover and while the sibling list loads.
  const chart = useMemo(
    () => (
      <ResponsiveContainer width="100%" height="100%" onResize={width => setChartWidth(width)}>
        <BarChart data={pageBars} margin={{ top: CHART_TOP_MARGIN, right: 0, left: 0, bottom: CHART_BOTTOM_MARGIN }}>
          <CartesianGrid vertical={false} stroke="#E9E9E9" />
          <XAxis
            dataKey="key"
            height={0}
            tick={false}
            axisLine={false}
            tickLine={false}
            padding={{ left: xAxisPadding, right: xAxisPadding }}
          />
          <YAxis
            width={Y_AXIS_WIDTH}
            axisLine={false}
            tickLine={false}
            tickSize={0}
            tickMargin={8}
            tickCount={5}
            // Figma labels every grid line, 0 included; the default interval drops ticks whose label
            // overflows the axis.
            interval={0}
            allowDecimals={false}
            tick={{ fontFamily: 'Dazzed', fontSize: 11, fontWeight: 500, fill: '#6D6D71' }}
          />
          <Bar dataKey="value" barSize={BAR_WIDTH} radius={[6, 6, 0, 0]}>
            {pageBars.map(bar => (
              <Cell key={bar.key} fill={bar.color} />
            ))}
            <LabelList dataKey="value" content={<BarValueLabel />} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    ),
    [pageBars, xAxisPadding]
  )

  const changePage = updatedPage => {
    setPage(updatedPage)
    onPageChanged(updatedPage)
  }

  return (
    <BaseWidget className={classes.root} carousel={false} isSelected={isSelected} removePadding>
      <div
        className={classes.container}
        tabIndex={0}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={e => !e.currentTarget.contains(e.relatedTarget) && setHovered(false)}
      >
        {isLoading ? (
          <EngagementWidgetSkeleton />
        ) : isError ? (
          <div className={classes.errorState}>{t('general.genericError')}</div>
        ) : (
          <>
            <div className={classes.title}>
              <DazzedHeadingForGrid regular xs={16} style={{ lineHeight: '22px' }}>
                {title}
              </DazzedHeadingForGrid>
              <Tooltip value={tooltip} containerClassName={classes.tooltip} placement="top" arrow variant="light">
                <ChartInfoIcon />
              </Tooltip>
            </div>
            <div className={classes.chartContainer}>
              {chart}
              {currentPage > 0 && (
                <button
                  type="button"
                  className={[classes.pageButton, classes.previousPageButton].join(' ')}
                  aria-label={t('pages.txTrackingReport.widgets.previousPage')}
                  onClick={() => changePage(currentPage - 1)}
                >
                  <ChartPageArrowIcon direction="previous" />
                </button>
              )}
              {currentPage < pagesCount - 1 && (
                <button
                  type="button"
                  className={[classes.pageButton, classes.nextPageButton].join(' ')}
                  aria-label={t('pages.txTrackingReport.widgets.nextPage')}
                  onClick={() => changePage(currentPage + 1)}
                >
                  <ChartPageArrowIcon direction="next" />
                </button>
              )}
            </div>
            <div className={classes.legend}>
              {/* Keep words intact and wrap whole legend items when the card cannot fit them on one row. */}
              {pageBars.map(bar => (
                <div key={bar.key} className={classes.legendItem}>
                  <LegendIcon color={bar.color} />
                  <span>{bar.name}</span>
                </div>
              ))}
            </div>
          </>
        )}
        {hasAnyData && !isLoading && !isError && <SeePatientsFooter visible={hovered} onClick={onSeePatients} />}
      </div>
    </BaseWidget>
  )
}

export default TxTrackingChartWidget
